import React, { ReactNode, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha, styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Zoom from "@mui/material/Zoom";
import { shallow } from "zustand/shallow";
import moment from "moment";
import CashflowPDF from "./CashflowPDF";
import styles from "../../Helpers/styles";
import { toINR } from "../../Helpers/functions";
import useCalc from "../../store/calc";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ICalculatedValues from "../../interfaces/ICalculatedValues";
import MailIcon from "@mui/icons-material/Mail";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";
import IBondDetails from "../../interfaces/IBondDetails";
import downloadCashflow from "../../Helpers/downloadCashflowFunction";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import { UserDetails } from "../RFQOrders/interfaces";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { sxBox, sxRoot, sxText, sxTypo } from "../../Helpers/helpers";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Listbox from "../Listbox";
import { makeGETRequest, makeGETRequestOnUserService } from "../../Api/api";
import { IUser } from "../YieldCalculator/Interfaces";
import { createQuotationPDF } from "./createQuotationPDF";
import { IAnalysisReport, INews } from "./interfaces";
import type { ILiquidityIndicator } from "./liquidityIndicator";
import toastr from "toastr";

interface IUserNameID {
  name: string;
  id: string;
}

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "#2A324C",
    borderRadius: "8px",
    fontSize: 12,
  },
}));

const SettlementAmount = ({
  calculatedValues,
}: {
  calculatedValues: ICalculatedValues;
}) => (
  <Box sx={styles.flexColumnCenterCenter}>
    <Typography
      sx={{
        color: `${alpha("#2A324C", 0.5)}`,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {toINR(calculatedValues?.price, 4)} @{calculatedValues?.Yield}%
    </Typography>
    <Typography
      variant="h6"
      noWrap
      sx={{
        color: "#2A324C",
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {toINR(calculatedValues?.investmentAmount, 2)}
    </Typography>
  </Box>
);

const Actions = () => {
  const [openShare, setOpenShare] = React.useState(false);
  const [openCopy, setOpenCopy] = React.useState(false);
  const [zoom, bondInfo, calculatedValues, breakup, getBreakup] = useCalc(
    (state: any) => [
      state.zoom,
      state.bondInfo,
      state.calculatedValues,
      state.breakup,
      state.getBreakup,
    ],
    shallow
  );

  const handleShareTooltipClose = () => {
    setOpenShare(false);
  };

  const handleShareTooltipOpen = () => {
    setOpenShare(true);
  };

  const subject = `Calculations for ${bondInfo?.isin}`;

  const text = `Hi, Checkout this bond price calculation on smest.in
*${bondInfo?.securityName} (${bondInfo?.isin})*
Rating: *${bondInfo?.ratingOrganizationMasters[0]?.rating?.rating} by ${
    bondInfo?.ratingOrganizationMasters[0]?.organization?.name
  }*
IP Frequency: *${bondInfo?.frequencyOfInterest}*
Type: *${
    bondInfo?.securedOrUnsecured === "Senior Secured" ? "Secured" : "Unsecured"
  }*
Face Value: *${toINR(bondInfo?.faceValue.split(" ")[0], 4)}*  

${bondInfo?.callOptions?.at(-1)?.date ? "Call Date" : "Maturity"}: ${
    bondInfo?.callOptions?.at(-1)?.date
      ? bondInfo?.callOptions?.at(-1)?.date
      : bondInfo?.maturities?.at(-1)?.date
  }
Coupon: ${bondInfo?.coupons[0].rate}%
No. of Bonds: *${calculatedValues?.noOfBonds}*
Price: *${toINR(calculatedValues?.price, 4)}*
Yield: *${calculatedValues?.Yield}%*
Settlement On: *${calculatedValues?.settlementDate.format("DD/MM/YYYY")}*
Principal Amount: ${toINR(calculatedValues?.principalAmount, 2)}
Accrued Interest: ${toINR(calculatedValues?.accruedInterest, 2)}
Stamp Duty: ${toINR(calculatedValues?.stampDuty, 2)}
Settlement Amount: *${toINR(calculatedValues?.investmentAmount, 2)}*`;

  return (
    <Box sx={styles.flexEndCenter}>
      {!zoom && (
        <Typography
          sx={{
            color: "#EC4F56",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onClick={() => getBreakup(!breakup)}
        >
          {breakup ? "Hide" : "View"} Breakup
        </Typography>
      )}
      <ClickAwayListener onClickAway={handleShareTooltipClose}>
        <div>
          <BootstrapTooltip
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleShareTooltipClose}
            open={openShare}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <Box sx={styles.flexBetweenCenter}>
                <BootstrapTooltip
                  title="Share via Whatsapp"
                  arrow
                  placement="bottom-end"
                  TransitionComponent={Zoom}
                >
                  <Box
                    component="a"
                    target="_blank"
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                      text
                    )}`}
                    sx={{ p: 0.5 }}
                  >
                    <WhatsAppIcon />
                  </Box>
                </BootstrapTooltip>
                <BootstrapTooltip
                  title="Share via Email"
                  arrow
                  placement="bottom-end"
                  TransitionComponent={Zoom}
                >
                  <Box
                    component="a"
                    target="_blank"
                    href={`mailto:?subject=${subject}&body=${encodeURIComponent(
                      text
                    )}`}
                    sx={{ p: 0.5 }}
                  >
                    <MailIcon />
                  </Box>
                </BootstrapTooltip>
              </Box>
            }
          >
            <IconButton
              sx={{
                p: 0,
                ml: 0.5,
              }}
              onClick={handleShareTooltipOpen}
            >
              <ShareIcon />
            </IconButton>
          </BootstrapTooltip>
        </div>
      </ClickAwayListener>
      <IconButton
        sx={{
          p: 0,
          ml: 0.5,
        }}
        onClick={() => copy(text)}
      >
        <ContentCopyIcon />
      </IconButton>
    </Box>
  );
};

export const Values = ({
  value,
  label,
}: {
  value: string | ReactNode;
  label: string;
}) => (
  <Box sx={{ mb: 1, width: "50%" }}>
    <Typography
      sx={{
        color: "#2A324C",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="h6"
      noWrap
      sx={{
        color: `${alpha("#2A324C", 0.5)}`,
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const Breakup = ({
  calculatedValues,
  bondInfo,
}: {
  calculatedValues: ICalculatedValues;
  bondInfo: IBondDetails;
}) => (
  <Box sx={{ ...styles.flexBetweenCenter, flexWrap: "wrap", mt: 2 }}>
    <Values value={`${calculatedValues?.noOfBonds}`} label="No. of Bonds" />
    <Values
      value={`${calculatedValues?.accruedDays}(${moment(
        bondInfo.lastIPDate,
        "YYYY-MM-DD"
      ).format("DD/MM/YYYY")})`}
      label="Accrued Days(Last IP Date)"
    />
    <Values
      value={`${toINR(calculatedValues?.principalAmount, 2)}`}
      label="Principal Amount"
    />
    <Values
      value={`${toINR(calculatedValues?.accruedInterest, 2)}`}
      label="Accrued Interest"
    />
    {!(
      bondInfo?.securityType.type === "State Development Loans" ||
      bondInfo?.securityType.type === "Government Securities" ||
      bondInfo?.securityType.type === "T Bill" ||
      bondInfo?.securityType.type === "Treasury Bill"
    ) && (
      <Values
        value={`${toINR(calculatedValues?.stampDuty, 2)}`}
        label="Stamp Duty"
      />
    )}
    <Values
      value={`${toINR(calculatedValues?.investmentAmount, 2)} @${
        calculatedValues?.Yield
      }%`}
      label="Settlement Amount"
    />
  </Box>
);

const Calculations = () => {
  const [zoom, bondInfo, calculatedValues, breakup, cashflow] = useCalc(
    (state) => [
      state.zoom,
      state.bondInfo,
      state.calculatedValues,
      state.breakup,
      state.cashflow,
    ],
    shallow
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [users, setUsers] = useState<readonly IUserNameID[]>([]);
  const [user, setUser] = useState<IUserNameID | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [useManualClientName, setUseManualClientName] = useState(false);
  const [manualClientName, setManualClientName] = useState("");
  const loadingUsers = userOpen && users.length === 0;

  const [analysisReport, setAnalysisReport] = useState<IAnalysisReport | null>(
    null
  );
  const [news, setNews] = useState<INews[] | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [analysisOk, setAnalysisOk] = useState(false);
  const [newsOk, setNewsOk] = useState(false);
  const [includeLiquidityIndicator, setIncludeLiquidityIndicator] = useState(false);
  const [liquidityState, setLiquidityState] = useState<{
    issuerName: string;
    data: ILiquidityIndicator | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const liquidityIssuerName = bondInfo?.issuerOfSecurity?.issuerName ?? "";
  const liquidityReady =
    liquidityState?.issuerName === liquidityIssuerName &&
    !!liquidityState?.data &&
    !liquidityState.loading;

  const closeQuotationDialog = () => {
    setDialogOpen(false);
    setIncludeLiquidityIndicator(false);
  };

  useEffect(() => {
    if (!dialogOpen || !includeLiquidityIndicator) {
      setLiquidityState(null);
      return;
    }

    if (!liquidityIssuerName) {
      setLiquidityState({
        issuerName: liquidityIssuerName,
        data: null,
        loading: false,
        error: "Issuer name is not available for the liquidity indicator.",
      });
      return;
    }

    let active = true;
    setLiquidityState({
      issuerName: liquidityIssuerName,
      data: null,
      loading: true,
      error: null,
    });

    makeGETRequestOnUserService(
      `order_management_v2/liquidity_indicator/?issuer_name=${encodeURIComponent(
        liquidityIssuerName
      )}`
    )
      .then((response) => {
        if (
          !response ||
          response.status !== 200 ||
          !response.data ||
          typeof response.data !== "object" ||
          Array.isArray(response.data)
        ) {
          throw new Error("Liquidity indicator is not available.");
        }
        if (active) {
          setLiquidityState({
            issuerName: liquidityIssuerName,
            data: response.data as ILiquidityIndicator,
            loading: false,
            error: null,
          });
        }
      })
      .catch(() => {
        if (active) {
          setLiquidityState({
            issuerName: liquidityIssuerName,
            data: null,
            loading: false,
            error:
              "Could not load liquidity indicator. Uncheck to continue, or check again to retry.",
          });
        }
      });

    return () => {
      active = false;
    };
  }, [dialogOpen, includeLiquidityIndicator, liquidityIssuerName]);

  useEffect(() => {
    let active = true;
    if (!loadingUsers) {
      return undefined;
    }
    makeGETRequestOnUserService("/user/user_list/")
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (active) {
            const optionsArray = (data as IUser[])
              .filter((item) => item.areKYCDetailsVerified)
              .filter(
                (item) =>
                  JSON.parse(localStorage.getItem("loginUser")!)?.isSuperuser ||
                  JSON.parse(localStorage.getItem("loginUser")!)?.can_see_all ||
                  item.leadowner ===
                    JSON.parse(localStorage.getItem("loginUser")!)?.name
              )
              .map((item) => ({
                name: `${item.firstName} ${item.lastName} - ${item.mobileNumber}`,
                id: item.uuid,
              }));
            setUsers([...optionsArray]);
          }
        }
      })
      .catch((error) => toastr.error(error));
    return () => {
      active = false;
    };
  }, [loadingUsers]);

  useEffect(() => {
    if (!userOpen) {
      setUsers([]);
    }
  }, [userOpen]);

  useEffect(() => {
    if (user) {
      makeGETRequestOnUserService(`user/get/${user.id}/`)
        .then((response) => {
          if (response && response.status === 200) {
            const { data } = response;
            setUserDetails(data);
          }
        })
        .catch((error) => toastr.error(error));
    } else {
      setUserDetails(null);
    }
  }, [user]);

  useEffect(() => {
    const issuerUUID = bondInfo?.issuerOfSecurity?.uuid;
    if (!dialogOpen || !issuerUUID) return;

    (async () => {
      // Analysis Report
      try {
        setAnalysisLoading(true);
        const response = await makeGETRequest(
          `admin/issuer_master/get/analysisReport/${issuerUUID}/`
        );
        if (response && response.status === 200) {
          setAnalysisReport(response.data as IAnalysisReport);
          setAnalysisOk(true);
        }
      } catch (error: any) {
        toastr.error(error.message);
        setAnalysisOk(false);
      } finally {
        setAnalysisLoading(false);
      }
    })();

    (async () => {
      // News (may return empty array)
      try {
        setNewsLoading(true);
        const response = await makeGETRequest(
          `admin/issuer_master_article/get/${issuerUUID}/`
        );
        if (response && response.status === 200) {
          setNews((response.data as INews[]) ?? []);
          setNewsOk(true);
        }
      } catch (error: any) {
        toastr.error(error.message);
        setNewsOk(false);
      } finally {
        setNewsLoading(false);
      }
    })();
  }, [dialogOpen, bondInfo?.issuerOfSecurity?.uuid]);

  return (
    <>
      {zoom ? (
        <Box>
          <Box
            sx={{
              p: 2,
              bgcolor: `${alpha("#EC4F56", 0.1)}`,
              borderRadius: "20px",
              mt: 1,
            }}
          >
            <SettlementAmount calculatedValues={calculatedValues!} />
          </Box>
          <Box sx={{ ...styles.flexBetweenCenter, mt: 1 }}>
            <Typography
              sx={{
                color: "#9397A4",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Breakup
            </Typography>
            <Actions />
          </Box>
          <Breakup calculatedValues={calculatedValues!} bondInfo={bondInfo!} />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: `${alpha("#EC4F56", 0.1)}`,
            borderRadius: "20px",
          }}
        >
          {breakup ? (
            <Box sx={styles.flexColumnStartEnd}>
              <Actions />
              <Breakup
                calculatedValues={calculatedValues!}
                bondInfo={bondInfo!}
              />
            </Box>
          ) : (
            <Box sx={{ ...styles.flexBetweenCenter, flexWrap: "wrap" }}>
              <SettlementAmount calculatedValues={calculatedValues!} />
              <Actions />
            </Box>
          )}
          <Box sx={{ ...styles.flexBetweenCenter, mt: 1, gap: 1 }}>
            <Box
              sx={{
                ...styles.flexCenterCenter,
                bgcolor: "#FFF",
                p: 1,
                pl: 2,
                borderRadius: 50,
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                sx={{
                  color: "#2A324C",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Download Cashflow
              </Typography>
              <BootstrapTooltip title="As PDF" arrow placement="top-end">
                <IconButton
                  sx={{
                    bgcolor: "secondary.main",
                    ml: 1,
                    boxShadow: "6px 6px 20px 0px #F33A4333",
                    ":hover": {
                      bgcolor: "secondary.main",
                    },
                  }}
                  onClick={() =>
                    bondInfo && calculatedValues
                      ? downloadCashflow(bondInfo, calculatedValues)
                      : toastr.error(`Error Generating Cashflow`)
                  }
                >
                  <PictureAsPdfIcon sx={{ color: "red" }} />
                </IconButton>
              </BootstrapTooltip>
            </Box>
            <Button
              variant="contained"
              size="small"
              sx={{ fontSize: 10 }}
              onClick={() => setDialogOpen(true)}
            >
              Propose Quotation
            </Button>
          </Box>
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={closeQuotationDialog}>
        <DialogTitle id="digilocker-choice-title">
          Select User to Propose Quotation
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            id={"user-select"}
            value={user}
            onChange={(_, newValue) => {
              setUser(newValue);
            }}
            open={userOpen}
            onOpen={() => {
              setUserOpen(true);
            }}
            onClose={() => {
              setUserOpen(false);
            }}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option.name}
            options={users}
            loading={loadingUsers}
            size="medium"
            sx={sxRoot}
            popupIcon={<ExpandMoreIcon />}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                sx={(theme) => sxText(theme)}
                id={"user-select"}
                fullWidth
                margin="dense"
                variant="standard"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? (
                        <CircularProgress color="error" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <Typography {...props} sx={sxTypo(selected)} variant="h6">
                {option.name}
              </Typography>
            )}
            PaperComponent={({ children }) => {
              return <Box sx={sxBox}>{children}</Box>;
            }}
            ListboxComponent={Listbox}
          />
          <Box sx={{ mt: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={useManualClientName}
                onChange={(e) => setUseManualClientName(e.target.checked)}
              />
              Enter client name manually
            </label>
            {useManualClientName && (
              <input
                type="text"
                placeholder="Client full name"
                value={manualClientName}
                onChange={(e) => setManualClientName(e.target.value)}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  border: "1px solid #D0D7DE",
                  flex: 1,
                }}
              />
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{ fontSize: 12, color: "#2A324C", fontWeight: 600 }}
            >
              {analysisLoading
                ? "Fetching analysis report…"
                : analysisOk
                ? "Analysis report ✓"
                : "Analysis report not loaded"}
            </Typography>
            <Typography
              sx={{ fontSize: 12, color: "#2A324C", fontWeight: 600 }}
            >
              {newsLoading
                ? "Fetching news…"
                : newsOk
                ? `News ✓ (${news?.length ?? 0})`
                : "News not loaded"}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#2A324C",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={includeLiquidityIndicator}
                  onChange={(e) =>
                    setIncludeLiquidityIndicator(e.target.checked)
                  }
                />
                Include liquidity indicator
              </label>
              {includeLiquidityIndicator && (
                <Typography
                  aria-live="polite"
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    color: liquidityState?.error ? "#EC4F56" : "#2A324C",
                  }}
                >
                  {liquidityState?.error ??
                    (liquidityReady
                      ? "Liquidity indicator loaded"
                      : "Fetching liquidity indicator...")}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 2, textAlign: "center", mx: "auto" }}>
          <Button
            variant="contained"
            disabled={
              (!userDetails &&
                !(useManualClientName && manualClientName.trim().length > 1)) ||
              !analysisOk ||
              !newsOk ||
              (includeLiquidityIndicator && !liquidityReady)
            }
            onClick={async () => {
              try {
                if (!bondInfo) return toastr.error("Bond not selected.");
                if (!calculatedValues)
                  return toastr.error("Please run a calculation first.");
                const usingManual =
                  useManualClientName && manualClientName.trim().length > 1;
                if (!usingManual && !userDetails) {
                  return toastr.error(
                    "Please select a user or enter a client name."
                  );
                }
                if (!cashflow)
                  return toastr.error("Cashflow data is not available.");
                if (!analysisReport)
                  return toastr.error("Analysis report is not available.");
                if (!news) return toastr.error("News is not available.");
                if (includeLiquidityIndicator && !liquidityReady)
                  return toastr.error("Liquidity indicator is not available.");

                await createQuotationPDF(
                  bondInfo,
                  calculatedValues,
                  {
                    ...(!usingManual ? (userDetails as any) : {}),
                    name: usingManual
                      ? manualClientName.trim()
                      : (userDetails as any)?.name ||
                        `${(userDetails as any)?.firstName ?? ""} ${
                          (userDetails as any)?.lastName ?? ""
                        }`.trim(),
                  } as any /*__MANUAL_NAME_INJECTED__*/,
                  cashflow,
                  analysisReport,
                  news,
                  includeLiquidityIndicator
                    ? liquidityState?.data ?? undefined
                    : undefined
                );
              } catch (e: any) {
                toastr.error(e?.message ?? "Failed to generate PDF");
                console.error(e);
              }
            }}
            sx={{
              color: "#FFFFFF",
              background: "linear-gradient(to right, #add100, #7b920a)",
              boxShadow:
                "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
              py: 1,
              px: 2,
              borderRadius: 2,
            }}
          >
            Propose Quotation for{" "}
            {useManualClientName && manualClientName.trim().length > 1
              ? manualClientName.trim()
              : userDetails
              ? userDetails.firstName
              : ""}
          </Button>
        </DialogActions>
      </Dialog>
      <CashflowPDF />
    </>
  );
};

export default Calculations;
